import { groupBy, uniq } from 'es-toolkit';

import {
  ComponentDesign,
  ComponentDesignLayoutModel,
  ComponentDesignSummary,
  Insertion,
  ModelCategory,
} from '@paragon/contract-library';

import { PathUtilities } from '../grid/PathUtilities';
import { getInsertionsWithPendingChanges } from '../selectors/changeTracker';
import { getChildrenFinder } from '../selectors/ChildrenFinder';
import { getDescendantPaths } from '../selectors/getDescendants';
import { getProjectAncestor } from '../selectors/getProjectAncestor';
import { LayoutModelsLibraryThunkAction } from '../store';
import { componentDesignsMoved } from '../store/changeTracker';
import { componentDesignsDeletedWithInsertions } from '../store/layoutModelsSlice';

export interface ComponentMovementRequest {
  targetContainerGuid: string;
  insertions: Insertion[];
  someLayoutComponentsIgnored: boolean;
  componentDesignGuidsInRelationships: string[];
}

export const moveComponentDesigns =
  ({ targetContainerGuid, insertions }: ComponentMovementRequest): LayoutModelsLibraryThunkAction<void> =>
  (dispatch, getState) => {
    const insertionsByProject = groupBy(
      insertions,
      (insertion) => getProjectAncestor(getState(), insertion.objectGuid).guid,
    );

    const targetProjectGuid = getProjectAncestor(getState(), targetContainerGuid).guid;
    const pathsToTargetContainer =
      targetContainerGuid === targetProjectGuid
        ? [targetProjectGuid]
        : uniq(getDescendantPaths(targetProjectGuid, getChildrenFinder(getState()))).filter(
            (path) => PathUtilities.getGuid(path) === targetContainerGuid,
          );

    dispatch(
      componentDesignsMoved({
        targetContainerGuid,
        pathsToTargetContainer,

        insertionsByProject,
      }),
    );
  };

export const componentDesignsDeleted =
  (componentDesignGuids: string | Set<string>): LayoutModelsLibraryThunkAction<void> =>
  (dispatch, getState) => {
    const state = getState();
    const targetGuidSet =
      typeof componentDesignGuids === 'string' ? new Set([componentDesignGuids]) : componentDesignGuids;

    const insertions = getInsertionsWithPendingChanges(state);

    dispatch(
      componentDesignsDeletedWithInsertions({
        guids: Array.from(targetGuidSet),
        insertions: insertions.filter((insertion) => targetGuidSet.has(insertion.objectGuid)),
      }),
    );
  };

export function componentDesignToLayoutModel(
  componentDesign: ComponentDesign | ComponentDesignSummary,
): ComponentDesignLayoutModel {
  return {
    type: 'componentDesign',
    modelType: 'ComponentDesign',
    category: ModelCategory.ComponentDesign,

    guid: componentDesign.guid,
    name: componentDesign.component.name,

    materialSelection: componentDesign.materialSelection,

    componentType: componentDesign.component.componentType,

    analogLocked: componentDesign.analogLocked,
    hangersLocked: componentDesign.hangersLocked,
    loadingLocked: componentDesign.loadingLocked,
    membersLocked: componentDesign.membersLocked,
    platingLocked: componentDesign.platingLocked,
  };
}
